import { z } from 'zod'

export const KeyStatsEarningSchema = z
  .object({
    date: z.string(),
    eps_actual: z.number().nullable().optional(),
    eps_estimate: z.number().nullable().optional(),
    eps_surprise_pct: z.number().nullable().optional(),
    revenue: z.number().nullable().optional(),
    revenue_est: z.number().nullable().optional(),
    revenue_surprise_percent: z.number().nullable().optional(),
    period: z.string().optional(),
    period_year: z.number().optional(),
    announce_time: z.string().optional(),
  })
  .passthrough()
export type KeyStatsEarning = z.infer<typeof KeyStatsEarningSchema>

// ─── Financial Time Series API Response ───

const FinancialPeriodSchema = z.record(z.unknown())

export const FinancialTimeSeriesResponseSchema = z
  .object({
    income_statement: z.array(FinancialPeriodSchema).optional(),
    balance_sheet: z.array(FinancialPeriodSchema).optional(),
    cash_flow: z.array(FinancialPeriodSchema).optional(),
  })
  .passthrough()
export type FinancialTimeSeriesResponse = z.infer<typeof FinancialTimeSeriesResponseSchema>

// ─── History Earnings API Response ───

export const HistoryEarningsEntrySchema = z
  .object({
    date: z.string(),
    eps_actual: z.number().nullable().optional(),
    eps_estimate: z.number().nullable().optional(),
    eps_surprise_pct: z.number().nullable().optional(),
    revenue: z.number().nullable().optional(),
    revenue_est: z.number().nullable().optional(),
    revenue_surprise_percent: z.number().nullable().optional(),
    revenue_prior: z.number().nullable().optional(),
    eps_surprise: z.number().nullable().optional(),
    revenue_surprise: z.number().nullable().optional(),
    importance: z.number().nullable().optional(),
    period: z.string().nullable().optional(),
    period_year: z.number().nullable().optional(),
    announce_time: z.string().nullable().optional(),
  })
  .passthrough()
export type HistoryEarningsEntry = z.infer<typeof HistoryEarningsEntrySchema>

// ─── Panel State ───

export type KeyStatsTab = 'overview' | 'financials' | 'earnings' | 'dilution'
// Re-exported from shared financials module for backwards compat
import type { FinancialSheet, FinancialPeriod } from '@/features/financials'
import type { ColorRule } from '@/features/sliced-form/schemas/profile-presets'
export type { FinancialSheet, FinancialPeriod }

// ─── Dilution Score ───

export type DilutionRating = 'low' | 'medium' | 'high'

export interface DilutionScore {
  overall: DilutionRating
  details?: {
    offeringRisk: DilutionRating
    ability: DilutionRating
    dilution: DilutionRating
    warrants: DilutionRating
    frequency: DilutionRating
    cashNeed: DilutionRating
    nasdaqCompliance: DilutionRating
  }
}

// ─── Why It's Moving ───

export interface WhyMovingItem {
  type: 'earnings' | 'news' | 'catalyst' | 'sector'
  headline: string
  time?: string
}

// ─── Column Groups ───

export interface KeyStatsColumnGroup {
  label: string
  columns: string[]
}

export interface KeyStatsPanelState {
  [key: string]: unknown
  activeTab?: KeyStatsTab
  columnGroups?: KeyStatsColumnGroup[]
  columnProfileId?: string | null
  financialSheet?: FinancialSheet
  financialPeriod?: FinancialPeriod
  /** Overview stat-tile background/text tint rules, keyed by column. Lives in
   *  panel state (like columnGroups), NOT the column profile. */
  colorRules?: ColorRule[]
}

// ─── Mock Data (dev fallback) ───

export const MOCK_DILUTION: DilutionScore = {
  overall: 'low',
  details: {
    offeringRisk: 'low',
    ability: 'low',
    dilution: 'low',
    warrants: 'low',
    frequency: 'low',
    cashNeed: 'low',
    nasdaqCompliance: 'low',
  },
}
